import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cx } from '@platform/core/lib/cx';
import { ModuleTile } from '@platform/ui';
import { routes } from '@platform/core/lib/routes';
import { useLabel } from '@platform/hooks/useLabel';
import { moduleNamespace } from '@platform/i18n/moduleBundle';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { catalogLabel, globalTypeNav, moduleNav, navGroup, typeNav } from '@platform/navigation';
import { useGetCatalogQuery, useMeQuery } from '@platform/state/api';
import type { IModuleOffFlags } from './IModuleOffFlags';
import type { ISettingsHeadProps } from './ISettingsHeadProps';
import { useGlobalModuleFacts } from './useGlobalModuleFacts';

/**
 * Шапка «Записів і форм» (прототип v4 pgCustModule): повернення контексту («← Модуль» / «← Модулі»), плитка 48, назва 22px, «Група ·
 * Модуль у просторі «…» · Увімкнено» (вимкнений тут чи глобально — «Вимкнено · записи збережено» без «Відкрити модуль»), «Відкрити модуль»
 * (повернення до модуля, прототип cdOpen — page:'module' без openModule); під нею —
 * опис модуля з поясненням, що модуль — це набір типів записів і форм. Три рядки — діти колонки сторінки. Дочірня таблиця (`rt`, окремий
 * модуль прототипу) — її назва, плитка й опис, «Відкрити модуль» — її список. На рівні «Глобально» (ADR-027) — «Категорія · Глобальне
 * визначення модуля · Увімкнено | Вимкнено всюди» (категорія й тон плитки — шаблону, categoryName; стан — глобальна доступність) без
 * «Відкрити модуль».
 */
export function SettingsModuleHead({ ws, module, rt, links, global }: ISettingsHeadProps) {
  const { t } = useTranslation();
  const label = useLabel();
  const strings = useModuleStrings(module.module.key);
  const { data: me } = useMeQuery();
  const key = module.module.key;
  const facts = useGlobalModuleFacts(key, undefined, !global);
  const { data: catalog } = useGetCatalogQuery({ workspaceId: ws }, { skip: !ws || global });
  const flags = catalog?.modules.find((m) => m.key === key) as IModuleOffFlags | undefined;
  const main = rt ?? module.recordTypes.find((r) => r.main);
  // Плитка глобального рівня — тон категорії шаблону (modIco прототипу: справи — «Основа», а не «Управління» простору).
  const nav = global && main ? globalTypeNav(key, main.key, module.module.category) : rt ? typeNav(key, rt.key) : moduleNav(key);
  const group = navGroup(moduleNav(key).group);
  // Глобальний рівень — категорія шаблону так, як її підписує прототип (рядок пакета categoryName), інакше — група навігації простору.
  const categoryName = global ? module.module.categoryName : undefined;
  const category = categoryName ? t('categoryName', { ns: moduleNamespace(key), defaultValue: label(categoryName) }) : group && label(catalogLabel(group));
  const name = rt ? strings.entity(rt.key, 'name', rt.name) : strings.moduleName(module.module.name);
  const desc = (main && strings.entity(main.key, 'description', main.description ?? null)) || (rt ? '' : strings.tagline(module.module.tagline));
  const open = rt ? routes.list(key, rt.key) : routes.moduleReturn(key);
  const scope = global ? t('modSettings.records.globalScope') : t('modSettings.records.scope', { name: me?.workspaces.find((w) => w.id === ws)?.name ?? '' });
  // Вимкнений модуль (прототип v4 modOff): глобально — «Вимкнено всюди»; у просторі (вимкнено тут чи глобально) — «Вимкнено · записи
  // збережено» і без «Відкрити модуль» (cdCanOpen = !G && !off).
  const off = global ? !facts.enabled : !!(flags?.disabled || flags?.globalDisabled);
  const state = off ? (global ? 'disabledEverywhere' : 'disabledKept') : 'enabled';
  return (
    <>
      <Link className="st-back" to={links.backTo}>{links.backLabel}</Link>
      <div className="st-head__row">
        <ModuleTile icon={nav.icon} tone={nav.tone} size={48} />
        <div className="st-head__text">
          <h1 className="st-head__title">{name}</h1>
          <div className="st-head__meta">
            {category && <><span>{category}</span><span>·</span></>}
            <span>{scope}</span><span>·</span>
            <span className={cx('st-head__state', off && 'st-head__state--off')}>{t(`modSettings.records.${state}`)}</span>
          </div>
        </div>
        {!global && !off && <Link className="st-head__btn" to={open}>{t('modSettings.records.open')}</Link>}
      </div>
      <p className="st-head__desc">{`${desc} ${t('modSettings.records.descSuffix')}`.trim()}</p>
    </>
  );
}
